import { toast } from "sonner";
import { errorMessage } from "@/api/client";
import { useReactivateOrganization, useSuspendOrganization } from "@/api/mutations";
import { ConfirmAction } from "@/components/feedback/confirm-dialog";

/** Suspend / reactivate with explicit confirmation (resource, action, consequences). */
export function OrganizationStatusAction({
  org,
  action,
  open,
  onOpenChange,
}: {
  org: { id: string; name: string };
  action: "suspend" | "reactivate";
  open: boolean;
  onOpenChange: (o: boolean) => void;
}) {
  const suspend = useSuspendOrganization(org.id);
  const reactivate = useReactivateOrganization(org.id);
  const m = action === "suspend" ? suspend : reactivate;
  return (
    <ConfirmAction
      open={open}
      onOpenChange={onOpenChange}
      action={action === "suspend" ? "Suspend organization" : "Reactivate organization"}
      resource={{ type: "Organization", name: org.name, id: org.id }}
      destructive={action === "suspend"}
      requireReason={action === "suspend"}
      requireTypedName={action === "suspend"}
      consequences={
        action === "suspend"
          ? [
              "All users in this organization lose access.",
              "Rogue stops messaging sellers for every store.",
              "Data is kept; you can reactivate later.",
              "Recorded in the audit log.",
            ]
          : ["Users regain access.", "Rogue resumes work on open conversations.", "Recorded in the audit log."]
      }
      confirmLabel={action === "suspend" ? "Suspend organization" : "Reactivate"}
      pending={m.isPending}
      onConfirm={(reason) => {
        const done = {
          onSuccess: () => {
            toast.success(action === "suspend" ? `${org.name} suspended` : `${org.name} reactivated`);
            onOpenChange(false);
          },
          onError: (err: unknown) => toast.error(action === "suspend" ? "Suspension failed" : "Reactivation failed", { description: errorMessage(err) }),
        };
        if (action === "suspend") suspend.mutate(reason, done);
        else reactivate.mutate(undefined, done);
      }}
    />
  );
}
